/**
 * Follow-up Reminder Service
 * This service handles automatic 2-week follow-up reminders for people who complete forms
 */

import reminderBackendApiService from './reminderBackendApiService';

class FollowUpReminderService {
  constructor() {
    this.isInitialized = false;
    this.init();
  }

  async init() {
    this.isInitialized = true;
    console.log('FollowUpReminderService initialized');
  }

  /**
   * Schedule a 2-week follow-up reminder when someone completes a form
   * @param {string} campaignId - Campaign ID
   * @param {string} appointmentId - Appointment ID
   * @param {string} name - Person's first name
   * @param {string} lastname - Person's last name
   * @param {string} phone - Person's phone number
   * @returns {Promise} - Results of the operation
   */
  async scheduleFollowUpReminder(campaignId, appointmentId, name, lastname, phone) {
    try {
      console.log(`Scheduling 2-week follow-up reminder for ${name} ${lastname}`);
      
      const result = await reminderBackendApiService.scheduleFollowUpReminder(
        campaignId,
        appointmentId,
        name,
        lastname,
        phone
      );
      
      console.log(`2-week follow-up reminder scheduled successfully for ${name} ${lastname}`);
      return result;
    } catch (error) {
      console.error('Error scheduling 2-week follow-up reminder:', error);
      throw error;
    }
  }

  /**
   * Stop follow-up reminders (called when regular reminder is set)
   * @param {string} campaignId - Campaign ID
   * @param {string} appointmentId - Appointment ID
   * @returns {Promise} - Results of the operation
   */
  async stopFollowUpReminder(campaignId, appointmentId) {
    try {
      // This would need a separate backend function to deactivate follow-up reminders
      // For now, we'll log it and the backend function will handle stopping automatically
      console.log(`Follow-up reminders will be automatically stopped for campaign ${campaignId}, appointment ${appointmentId} when regular reminder is activated`);
      return { message: 'Follow-up reminders will be stopped automatically when regular reminder is set' };
    } catch (error) {
      console.error('Error stopping follow-up reminder:', error);
      throw error;
    }
  }

  /**
   * Handle form completion - automatically schedule 2-week follow-up
   * @param {object} appointmentData - The appointment data from form completion
   * @returns {Promise} - Results of the operation
   */
  async handleFormCompletion(appointmentData) {
    try {
      const { campaignId, appointmentId, name, lastname, phone } = appointmentData;
      
      if (!campaignId || !appointmentId || !name || !lastname || !phone) {
        throw new Error('Missing required appointment data for follow-up reminder');
      }

      return await this.scheduleFollowUpReminder(campaignId, appointmentId, name, lastname, phone);
    } catch (error) {
      console.error('Error handling form completion for follow-up reminder:', error);
      throw error;
    }
  }

  /**
   * Check if follow-up reminders should be started for existing appointments
   * @param {string} campaignId - Campaign ID
   * @param {string} appointmentId - Appointment ID
   * @param {object} appointmentData - The appointment data
   * @returns {Promise} - Results of the operation
   */
  async checkAndStartFollowUp(campaignId, appointmentId, appointmentData) {
    try {
      // Check if this appointment already has a regular reminder
      const hasRegularReminder = appointmentData.reminder === true;
      const hasFollowUpReminder = appointmentData.followUpReminder === true;
      
      // Only start follow-up if no regular reminder and no follow-up already exists
      if (!hasRegularReminder && !hasFollowUpReminder) {
        return await this.handleFormCompletion({
          campaignId,
          appointmentId,
          name: appointmentData.name,
          lastname: appointmentData.lastname,
          phone: appointmentData.phone
        });
      }
      
      return { message: 'Follow-up reminder not needed - other reminders already exist' };
    } catch (error) {
      console.error('Error checking and starting follow-up reminder:', error);
      throw error;
    }
  }

  /**
   * Get configuration
   * @returns {object} - Service configuration
   */
  getConfig() {
    return {
      serviceName: 'FollowUpReminderService',
      followUpInterval: '2 weeks',
      isInitialized: this.isInitialized
    };
  }
}

// Create and export singleton instance
const followUpReminderService = new FollowUpReminderService();
export default followUpReminderService;
